/**
 * GIWAHS Adaptive Journey — Canonical Initial Registrations
 * ---------------------------------------------------------------------
 * Per AJIP-16.02.003 · WS2 and Founder Ruling 03:
 *   These are INITIAL REGISTRATIONS, not architectural constants.
 *   The Journey Registry accepts arbitrary future registrations through
 *   configuration. The Core Engine is never modified to add new
 *   journey types.
 *
 * Each entry is a valid JourneyDefinition (see JourneyConfig.js) and
 * describes states, entry/exit conditions, transitions, features and
 * permissions. Behaviour (UI, AI, persistence) is intentionally
 * excluded from this package.
 *
 * Registration
 *   registerCanonicalJourneys(engine) — registers all six on the given
 *   engine. Idempotent: skips identifiers already present. Callers may
 *   also register a subset by calling engine.register() directly.
 */
import { STATES } from '../core/JourneyState';

const S = STATES;

const founderJourney = Object.freeze({
  identifier: 'founder',
  name: 'Founder Journey',
  description: 'Orchestrates the Founder discovery, validation and onboarding pathway.',
  entryConditions: ['is_prospective_founder'],
  exitConditions: ['founder_onboarded', 'application_declined'],
  availableStates: [S.NOT_STARTED, S.STARTED, S.IN_PROGRESS, S.WAITING, S.BLOCKED, S.COMPLETED, S.CANCELLED, S.FAILED],
  transitions: [
    { from: S.STARTED,      event: 'proceed',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS,  event: 'wait',      to: S.WAITING     },
    { from: S.WAITING,      event: 'resume',    to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS,  event: 'block',     to: S.BLOCKED     },
    { from: S.BLOCKED,      event: 'unblock',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS,  event: 'complete',  to: S.COMPLETED   },
    { from: S.IN_PROGRESS,  event: 'cancel',    to: S.CANCELLED   },
    { from: S.IN_PROGRESS,  event: 'fail',      to: S.FAILED      },
  ],
  enabledFeatures: [],
  requiredPermissions: [],
});

const buyerJourney = Object.freeze({
  identifier: 'buyer',
  name: 'Buyer Journey',
  description: 'Orchestrates discovery, comparison, RFQ and decision flows for buyers.',
  entryConditions: ['is_authenticated_or_anonymous_buyer'],
  exitConditions: ['rfq_submitted', 'quote_accepted', 'session_ended'],
  availableStates: [S.NOT_STARTED, S.STARTED, S.IN_PROGRESS, S.WAITING, S.BLOCKED, S.COMPLETED, S.CANCELLED, S.FAILED],
  transitions: [
    { from: S.STARTED,     event: 'proceed',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'wait',     to: S.WAITING     },
    { from: S.WAITING,     event: 'resume',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'complete', to: S.COMPLETED   },
    { from: S.IN_PROGRESS, event: 'cancel',   to: S.CANCELLED   },
    { from: S.IN_PROGRESS, event: 'fail',     to: S.FAILED      },
  ],
  enabledFeatures: [],
  requiredPermissions: [],
});

const supplierJourney = Object.freeze({
  identifier: 'supplier',
  name: 'Supplier Journey',
  description: 'Orchestrates supplier claim, verification, quote and profile management flows.',
  entryConditions: ['is_supplier_principal'],
  exitConditions: ['profile_published', 'claim_declined'],
  availableStates: [S.NOT_STARTED, S.STARTED, S.IN_PROGRESS, S.WAITING, S.BLOCKED, S.COMPLETED, S.CANCELLED, S.FAILED],
  transitions: [
    { from: S.STARTED,     event: 'proceed',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'wait',     to: S.WAITING     },
    { from: S.WAITING,     event: 'resume',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'block',    to: S.BLOCKED     },
    { from: S.BLOCKED,     event: 'unblock',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'complete', to: S.COMPLETED   },
    { from: S.IN_PROGRESS, event: 'cancel',   to: S.CANCELLED   },
    { from: S.IN_PROGRESS, event: 'fail',     to: S.FAILED      },
  ],
  enabledFeatures: [],
  requiredPermissions: [],
});

const factoryJourney = Object.freeze({
  identifier: 'factory',
  name: 'Factory Journey',
  description: 'Orchestrates factory-side capacity, verification and production readiness flows.',
  entryConditions: ['is_factory_principal'],
  exitConditions: ['factory_active', 'application_declined'],
  availableStates: [S.NOT_STARTED, S.STARTED, S.IN_PROGRESS, S.WAITING, S.BLOCKED, S.COMPLETED, S.CANCELLED, S.FAILED],
  transitions: [
    { from: S.STARTED,     event: 'proceed',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'wait',     to: S.WAITING     },
    { from: S.WAITING,     event: 'resume',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'complete', to: S.COMPLETED   },
    { from: S.IN_PROGRESS, event: 'cancel',   to: S.CANCELLED   },
    { from: S.IN_PROGRESS, event: 'fail',     to: S.FAILED      },
  ],
  enabledFeatures: [],
  requiredPermissions: [],
});

const brandJourney = Object.freeze({
  identifier: 'brand',
  name: 'Brand Journey',
  description: 'Orchestrates brand discovery, brand-to-factory intelligence and sourcing engagement flows.',
  entryConditions: ['is_brand_principal'],
  exitConditions: ['brand_engaged', 'session_ended'],
  availableStates: [S.NOT_STARTED, S.STARTED, S.IN_PROGRESS, S.WAITING, S.BLOCKED, S.COMPLETED, S.CANCELLED, S.FAILED],
  transitions: [
    { from: S.STARTED,     event: 'proceed',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'wait',     to: S.WAITING     },
    { from: S.WAITING,     event: 'resume',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'complete', to: S.COMPLETED   },
    { from: S.IN_PROGRESS, event: 'cancel',   to: S.CANCELLED   },
    { from: S.IN_PROGRESS, event: 'fail',     to: S.FAILED      },
  ],
  enabledFeatures: [],
  requiredPermissions: [],
});

const administratorJourney = Object.freeze({
  identifier: 'administrator',
  name: 'Administrator Journey',
  description: 'Orchestrates administrator moderation, verification review and platform operations flows.',
  entryConditions: ['is_administrator'],
  exitConditions: ['session_ended'],
  availableStates: [S.NOT_STARTED, S.STARTED, S.IN_PROGRESS, S.WAITING, S.BLOCKED, S.COMPLETED, S.CANCELLED, S.FAILED],
  transitions: [
    { from: S.STARTED,     event: 'proceed',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'wait',     to: S.WAITING     },
    { from: S.WAITING,     event: 'resume',   to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'block',    to: S.BLOCKED     },
    { from: S.BLOCKED,     event: 'unblock',  to: S.IN_PROGRESS },
    { from: S.IN_PROGRESS, event: 'complete', to: S.COMPLETED   },
    { from: S.IN_PROGRESS, event: 'cancel',   to: S.CANCELLED   },
    { from: S.IN_PROGRESS, event: 'fail',     to: S.FAILED      },
  ],
  enabledFeatures: [],
  requiredPermissions: [],
});

const CANONICAL_JOURNEYS = Object.freeze([
  founderJourney,
  buyerJourney,
  supplierJourney,
  factoryJourney,
  brandJourney,
  administratorJourney,
]);

const registerCanonicalJourneys = (engine) => {
  const registered = [];
  for (const def of CANONICAL_JOURNEYS) {
    if (!engine.has(def.identifier)) {
      engine.register(def);
      registered.push(def.identifier);
    }
  }
  return Object.freeze(registered);
};

export { CANONICAL_JOURNEYS, registerCanonicalJourneys };
